import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import {
  GroupBase,
  MenuListProps,
  NoticeProps,
  components,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { fetchJobsList } from '../../api/admin';
import calendarIcon from '../../assets/icons/new_icon/calendar.svg';
import { formatDateWithDashes } from '../../utils/helper';
import DateRangePicker from './litePicker';

interface Props {
  selectedJob: any;
  setSelectedJob?: any;
  setSelectedJobId?: any;
  selectedAccount?: any;
  dummyOption?: string;
  isAdmin?: boolean;
  setHandleToggleChange?: any;
  label?: any;
}

const SelectJobDropdown = ({
  selectedJob,
  setSelectedJob,
  setSelectedJobId,
  selectedAccount,
  dummyOption,
  setHandleToggleChange,
}: Props) => {
  const [jobsList, setJobsList] = useState<any>([]);
  const [dateRange, setDateRange] = useState<{
      startDate: Date;
      endDate: Date;
    }>({
      startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      endDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [key, setKey] = useState(0);

  const loadOptions = (
    inputValue: string,
    callback: (options: any) => void,
    pageNo?: number
  ) => {
    fetchJobsList({
      is_pagination: true,
      search: inputValue,
      page: inputValue ? 1 : pageNo ? pageNo : currentPage,
      account: selectedAccount,
      status: dummyOption ? 'IN_PROGRESS,CREATED' : undefined,
      startDate: dateRange.startDate
        ? formatDateWithDashes(
            new Date(dateRange.startDate)
          )
        : '',
      endDate: dateRange.endDate
        ? formatDateWithDashes(
            new Date(dateRange.endDate)
          )
        : '',
    })
      .then((response) => {
        if (!response?.data?.is_error) {
          let jobsData = response?.data?.content?.data;
          const formattedOptions = jobsData.map((item: any) => ({
            label: item?.bbe_number,
            value: item?.bbe_number,
          }));
          let newList: any = [];
          if (
            dummyOption &&
            formattedOptions.length > 0 &&
            jobsList.length === 0
          ) {
            const newOption = {
              label: dummyOption,
              value: '',
            };
            newList = [newOption, ...formattedOptions];
          } else {
            newList =
              pageNo && pageNo === 1
                ? formattedOptions
                : [...jobsList, ...formattedOptions];
          }

          setTotalPages(response?.data?.content?.pages);

          setJobsList(newList);
          if (inputValue) {
            callback(formattedOptions);
          } else {
            callback(newList);
          }

          setIsLoading(false);
        }
      })
      .catch((e) => {
        callback([]);
      });
  };

  const debouncedLoadOptions = debounce(loadOptions, 300);

  const handleMenuScrollToBottom = () => {
    if (!isLoading && totalPages > currentPage) {
      setIsLoading(true);
      setKey(key + 1);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const onChangeDateRange = (startDate: Date, endDate: Date) => {
    setDateRange({ startDate, endDate });
    setSelectedJob('');
    loadOptions('', () => {}, currentPage);
  };

  useEffect(() => {
    setJobsList([]);
    setCurrentPage(1);
    setKey(key + 1);
  }, [selectedAccount]);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      border: '1px solid black !important',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#212529 !important',
      boxShadow: state.isFocused
        ? '0 0 0 0.3rem rgba(173, 216, 230, 0.50)'
        : 'none',
      backgroundColor: '#ffffff',
      paddingTop: '3px',
      paddingBottom: '3px',
      marginRight: '16px',
      fontFamily: 'Lexend',
      height: 48,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      '& svg': {
        width: '20px',
        height: '20px',
        color: '#25215F',
      },
    }),
  };

  const optionStyles = {
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#ddd' : 'transparent',
      padding: '8px 20px',
      fontSize: 14,
      zIndex: 1,
      color: '#212529',
    }),
  };

  const MenuList = (props: MenuListProps<any, boolean, GroupBase<unknown>>) => {
    const { maxHeight, ...rest } = props;
    const menuHeight = Math.min(maxHeight, 200); // Set a max height for the menu
    return (
      <components.MenuList {...rest} maxHeight={menuHeight}>
        {props.children}
        {isLoading && (
          <div className='spinner-border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        )}
      </components.MenuList>
    );
  };

  const handleChange = (selectedOption: any) => {
    setSelectedJob && setSelectedJob(selectedOption.value);
    setSelectedJobId && setSelectedJobId(selectedOption.value);
    setHandleToggleChange && setHandleToggleChange(true);
  };
  const NoOptionsMessage: React.FC<
    NoticeProps<any, boolean, GroupBase<unknown>>
  > = (props) => (
    <components.NoOptionsMessage {...props}>
      No options found
    </components.NoOptionsMessage>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <div style={{ width: '230px' }}>
        <AsyncSelect
          id={'jobs-dropdown'}
          key={key}
          cacheOptions
          loadOptions={debouncedLoadOptions}
          defaultOptions
          onMenuScrollToBottom={handleMenuScrollToBottom}
          styles={{ ...customStyles, ...optionStyles }}
          placeholder={dummyOption ? dummyOption : 'Select a value'}
          onChange={handleChange}
          value={selectedJob ? { label: selectedJob, value: selectedJob } : null}
          components={{
            NoOptionsMessage,
            MenuList,
          }}
        />
      </div>
      <div
        style={{
          width: '247px',
          transform: 'translateY(-30px)'
        }}
      >
        <label htmlFor='job-date-range' className='form-label labelText'>
          Job Date
        </label>
        <DateRangePicker
          id='job-date-range'
          className='form-control ubbe-input'
          selectedDates={dateRange}
          onChange={onChangeDateRange}
          calendarIcon={calendarIcon}
          setHandleToggleChange={setHandleToggleChange}
        />
      </div>
    </div>
    
  );
};

export default SelectJobDropdown;
